import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormGroupDirective, Validators } from "@angular/forms";
import { Observable, of, Subscription } from "rxjs";
import { Yii } from "libraries/interfaces/Yii";
import { ICheckboxFieldSpecs, IFieldSpecs } from "libraries/module/formbuilder/fields/interfaces/field-specs";
import { HostAliasResource, IHostAlias } from "libraries/service/resources/host-alias/host-alias.resource";
import { ConfigurationResourceService } from "libraries/service/resources/configuration/configurationResource.service";
import { IMultiselectOptions } from "libraries/module/formbuilder/fields/multiselect/multiselect.model";
import { switchMap, tap } from 'rxjs/operators';
import { IAirFormConfig } from "libraries/module/formbuilder/forms/searchAirTrip/searchAirTrip.component";
import { EHosts } from "libraries/enums/EHosts";
import { EAirSearchFields } from "app_locobook/air-search/e-air-search-fields";
import { ILabelAndValue } from "libraries/interfaces/ILabelAndValue";
import { AirSearchHeaderActionService } from "app_locobook/air-search/air-search-header/air-search-header-action/air-search-header-action.service";
import { AirSearchHostFormHelperService } from "app_locobook/air-search/air-search-form/air-search-host-form/air-search-host-form-helper.service";


class Wording {
    public readonly host: string = Yii.t('wui-shared-js', "Sources");
    public readonly restriction: string = Yii.t('wui-shared-js', 'Restriction');
    public readonly checkboxLabel: string = Yii.t('wui-shared-js', 'TRAVELPORT vol régulier uniquement');
}

@Component({
    selector: 'air-search-host-form',
    templateUrl: './air-search-host-form.component.html',
    styleUrls: ['./air-search-host-form.component.scss']
})
export class AirSearchHostFormComponent implements OnInit, OnDestroy {
    @Input()
    public readonlyParams: boolean = false;
    public hostsList: IMultiselectOptions[] = [];
    public excludeLowCostFieldSpec: ICheckboxFieldSpecs;
    public hostFieldSpec: IFieldSpecs;
    public wording: Wording = new Wording();
    public airSearchForm: FormGroup;

    private configurationRessourceServiceSubscription: Subscription;
    private airSearchHostFormSubscription: Subscription;
    private airSearchResetSubscription: Subscription;
    private hostsConfig: string[];

    constructor(
        private readonly configurationResourceService: ConfigurationResourceService,
        private readonly hostAliasResource: HostAliasResource,
        private readonly formGroupDirective: FormGroupDirective,
        private readonly airSearchHeaderActionService: AirSearchHeaderActionService,
        private readonly airSearchHostFormHelperService: AirSearchHostFormHelperService,
    ) {
    }

    public ngOnInit(): void {
        this.initAirSearchForm();
        this.initializeFormConfig();
        this.initHostSubscribe();
        this.initAirSearchResetSubscription();
    }

    public ngOnDestroy(): void {
        if (this.airSearchHostFormSubscription) {
            this.airSearchHostFormSubscription.unsubscribe();
        }
        if (this.airSearchResetSubscription) {
            this.airSearchResetSubscription.unsubscribe();
        }
        if (this.configurationRessourceServiceSubscription) {
            this.configurationRessourceServiceSubscription.unsubscribe();
        }
    }

    public toggleFullServiceOption(isTravelportSelected: boolean): void {
        this.hostsList = this.airSearchHostFormHelperService.manageFullServiceCarriersOnlyInHostList(
            isTravelportSelected,
            this.hostsList,
            this.wording.checkboxLabel
        );
    }

    private initAirSearchForm(): void {
        this.airSearchForm = this.formGroupDirective.form;
        this.airSearchForm.addControl(EAirSearchFields.host, new FormControl());
        this.airSearchForm.addControl(EAirSearchFields.fullServiceCarriersOnly, new FormControl(false));
        this.excludeLowCostFieldSpec = {
            formGroup: this.airSearchForm,
            formControlName: EAirSearchFields.fullServiceCarriersOnly,
            checkboxLabel: this.wording.checkboxLabel
        };

        this.hostFieldSpec = {
            formGroup: this.airSearchForm,
            formControlName: EAirSearchFields.host,
            validators: [
                {
                    validator: Validators.required,
                    errors: {
                        required: {
                            when: "touched",
                            message: Yii.t('wui-shared-js', 'Source requise'),
                        }
                    }
                }
            ],
            disabled: this.readonlyParams
        };
    }

    private initializeFormConfig(): void {
        this.configurationRessourceServiceSubscription = this.configurationResourceService
            .get(['wui', 'app_locobook', 'config', 'forms', 'form_locobook.json'], {})
            .pipe(
                tap((formConfig: IAirFormConfig): void => {
                    this.hostsConfig = formConfig?.selectedHosts;
                }),
                switchMap((formConfig: IAirFormConfig): Observable<IHostAlias[]> => {
                    if (formConfig) {
                        return this.hostAliasResource.getAliases(formConfig.hosts);
                    }
                    return of([]);
                })
            )
            .subscribe((hostAliases: IHostAlias[]): void => {
                this.hostsList = this.airSearchHostFormHelperService.transformHostsResponseToLabelValue(hostAliases);
                if (this.hostsConfig?.length && !this.airSearchForm.get(EAirSearchFields.host)?.value?.length) {
                    this.airSearchForm.get(EAirSearchFields.host).setValue(this.hostsConfig);
                }
                const isTravelportSelected: boolean = this.hostsConfig?.includes(EHosts.TRAVELPORT);
                this.toggleFullServiceOption(isTravelportSelected);
            });
    }


    private initHostSubscribe(): void {
        this.airSearchHostFormSubscription = this.airSearchForm.get(EAirSearchFields.host).valueChanges.subscribe((values: string[]): void => {
            const isTravelportSelected: boolean = values?.includes(EHosts.TRAVELPORT);
            const isFullServiceCarriersOnlyChecked: boolean = values?.includes(EAirSearchFields.fullServiceCarriersOnly);
            // need remove fullServiceCarrierOnly from form value
            if (!isTravelportSelected && isFullServiceCarriersOnlyChecked) {
                const airSearchHostFormValueWithoutFullServiceCarrierOnly: string[] = this.airSearchForm.get(EAirSearchFields.host).value.filter((host: string) => host !== EAirSearchFields.fullServiceCarriersOnly);
                this.airSearchForm.get(EAirSearchFields.host).setValue(airSearchHostFormValueWithoutFullServiceCarrierOnly, {emitEvent: false})
            }
            this.toggleFullServiceOption(isTravelportSelected);
            this.airSearchForm.get(EAirSearchFields.fullServiceCarriersOnly).setValue(isTravelportSelected ? isFullServiceCarriersOnlyChecked : false);
        });
    }

    private initAirSearchResetSubscription(): void {
        this.airSearchResetSubscription =
            this.airSearchHeaderActionService.resetAirSearchFormObservable.subscribe(() => {
                this.airSearchForm.get(EAirSearchFields.host).setValue(this.hostsConfig);
            });
    }
}